import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Bell, Smartphone, Mail, MessageCircle, CheckCircle2, AlertCircle, XCircle } from "lucide-react";
import {
  enablePushNotifications, disablePushNotifications, sendPushSelfTest,
  pushSupported, pushPermission, isIOS, isRunningStandalone,
} from "@/lib/push";

/**
 * Self-service notification settings page.
 * Route: /settings/notifications (available to all authenticated users).
 * Ignores the 14-day auto-banner cooldown — user can always enable/disable here.
 */
export default function NotificationSettingsPage() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    try {
      const { data } = await api.get("/notifications/push/status");
      setStatus(data);
    } catch { /* ignore */ }
  };

  useEffect(() => { refresh(); }, []);

  const supported = pushSupported();
  const perm = pushPermission();
  const running_standalone = isRunningStandalone();
  const on_ios = isIOS();

  const handleEnable = async () => {
    setBusy(true);
    const res = await enablePushNotifications();
    setBusy(false);
    if (res.ok) {
      toast.success("Notifications enabled");
      refresh();
    } else if (res.reason === "denied") {
      toast.error("Notifications blocked. Re-enable in your browser/OS settings.");
    } else if (res.reason === "unsupported") {
      toast.error("Your browser doesn't support push notifications.");
    } else {
      toast.error(`Could not enable: ${res.reason}`);
    }
  };

  const handleDisable = async () => {
    setBusy(true);
    await disablePushNotifications();
    setBusy(false);
    toast.success("Notifications disabled");
    refresh();
  };

  const handleTest = async () => {
    setBusy(true);
    try {
      const r = await sendPushSelfTest();
      setBusy(false);
      if (r.sent > 0) {
        toast.success(`Test push sent to ${r.sent} device(s). Check your notifications now.`);
      } else if (r.gone > 0) {
        toast.error(`All ${r.gone} device(s) marked inactive. Please re-enable.`);
        refresh();
      } else {
        toast.error(`Could not send: ${r.reason || "unknown"}`);
      }
    } catch (e) {
      setBusy(false);
      toast.error(e?.response?.data?.detail || "Test failed");
    }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6" data-testid="notification-settings-page">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Bell className="h-6 w-6 text-secondary" />
          Notification Settings
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Choose how you want to receive updates about your child&apos;s classes.
        </p>
      </div>

      {/* PUSH */}
      <Card data-testid="push-settings-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Smartphone className="h-5 w-5 text-secondary" />
            App Push Notifications
            {status?.active_devices > 0 && (
              <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-300">
                Active on {status.active_devices} device{status.active_devices > 1 ? "s" : ""}
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Instant alerts on your phone&apos;s lock screen. <strong>Free forever.</strong>
          </p>

          {/* Diagnostic panel — helps user & you figure out issues */}
          <div className="rounded-md bg-muted/50 p-3 space-y-1.5 text-xs">
            <Row label="Browser supports push"
                 ok={supported}
                 hint={!supported ? "Try Chrome/Edge/Firefox on Android, or Safari on iOS 16.4+" : ""} />
            <Row label="Server configured"
                 ok={!!status?.configured}
                 hint={!status?.configured ? "Server has no VAPID keys yet" : ""} />
            <Row label="Permission state"
                 raw={perm === "granted" ? "ok" : perm === "denied" ? "bad" : "warn"}
                 value={perm}
                 hint={perm === "denied"
                   ? "You blocked notifications. Re-enable in browser/OS settings for this site."
                   : ""} />
            {on_ios && (
              <Row label="Running as installed PWA"
                   ok={running_standalone}
                   hint={!running_standalone
                     ? "iOS requires installing to Home Screen first. Tap Share → Add to Home Screen, then open the app from your Home Screen icon."
                     : ""} />
            )}
            <Row label="Active devices for your account"
                 raw={(status?.active_devices || 0) > 0 ? "ok" : "warn"}
                 value={String(status?.active_devices || 0)} />
          </div>

          <div className="flex flex-wrap gap-2 pt-2">
            {status?.active_devices > 0 ? (
              <>
                <Button variant="outline" onClick={handleDisable} disabled={busy}
                        data-testid="disable-push-btn">
                  Disable push
                </Button>
                <Button onClick={handleTest} disabled={busy} data-testid="test-push-btn">
                  Send a test push
                </Button>
              </>
            ) : (
              <Button onClick={handleEnable} disabled={busy || !supported || perm === "denied"}
                      data-testid="enable-push-btn">
                {busy ? "Working…" : "Enable push notifications"}
              </Button>
            )}
          </div>

          {on_ios && !running_standalone && (
            <div className="mt-3 rounded-md bg-amber-50 border border-amber-200 p-3 text-xs">
              <p className="font-semibold text-amber-900 mb-1.5">iPhone / iPad — install this app first</p>
              <ol className="space-y-0.5 text-amber-900">
                <li>1. Tap the Share icon (↑) at the bottom of Safari</li>
                <li>2. Scroll down and tap &quot;Add to Home Screen&quot;</li>
                <li>3. Tap &quot;Add&quot; in the top-right</li>
                <li>4. Close Safari and open the app from your Home Screen</li>
                <li>5. Come back to this page and tap &quot;Enable push notifications&quot;</li>
              </ol>
            </div>
          )}
        </CardContent>
      </Card>

      {/* EMAIL */}
      <Card data-testid="email-settings-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Mail className="h-5 w-5 text-secondary" />
            Email Notifications
            <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-300">Always on</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            Every important notification is also sent to your registered email address.
            Full details with attachments and receipts. Free forever.
          </p>
        </CardContent>
      </Card>

      {/* WHATSAPP */}
      <Card data-testid="whatsapp-settings-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <MessageCircle className="h-5 w-5 text-secondary" />
            WhatsApp Notifications
            <Badge variant="outline">Coming soon</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            Urgent alerts (class cancelled, payment overdue) will also arrive on your WhatsApp
            once we complete the Meta approval process.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

function Row({ label, ok, raw, value, hint }) {
  const state = raw || (ok ? "ok" : "bad");
  const Icon = state === "ok" ? CheckCircle2 : state === "warn" ? AlertCircle : XCircle;
  const color = state === "ok" ? "text-emerald-600" : state === "warn" ? "text-amber-600" : "text-destructive";
  return (
    <div className="flex items-start gap-2">
      <Icon className={`h-3.5 w-3.5 mt-0.5 shrink-0 ${color}`} />
      <div className="flex-1">
        <div className="flex justify-between gap-2">
          <span>{label}</span>
          {value !== undefined && <span className="font-mono">{value}</span>}
        </div>
        {hint && <div className="text-muted-foreground text-[10px] mt-0.5">{hint}</div>}
      </div>
    </div>
  );
}
